import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { CHANNEL_TYPE_LABELS, ChannelType } from '../../types/atendimentoEnums';
import { ChannelTypeIcon } from './ChannelTypeIcon';

interface ChannelTypeSelectProps {
  value: ChannelType;
  onChange: (channelType: ChannelType) => void;
  disabled?: boolean;
}

const CHANNEL_OPTIONS = Object.entries(CHANNEL_TYPE_LABELS).map(([channelValue, label]) => ({
  value: channelValue as ChannelType,
  label,
}));

export const ChannelTypeSelect: React.FC<ChannelTypeSelectProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const selectedLabel = CHANNEL_TYPE_LABELS[value];

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <Listbox.Button
          className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-left text-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
        >
          <span className="flex items-center gap-2 min-w-0">
            <ChannelTypeIcon channelType={value} className="h-4 w-4 flex-shrink-0" />
            <span className="block truncate font-medium text-gray-900">{selectedLabel}</span>
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
            {CHANNEL_OPTIONS.map((option) => (
              <Listbox.Option key={option.value} value={option.value} as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative flex cursor-pointer select-none items-center gap-2 px-3 py-2 ${
                      selected || active ? 'bg-blue-50' : ''
                    }`}
                  >
                    <ChannelTypeIcon channelType={option.value} className="h-4 w-4 flex-shrink-0" />
                    <span className={`flex-1 truncate text-gray-900 ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {option.label}
                    </span>
                    {selected && (
                      <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                    )}
                  </li>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};
